/**
 * RemoteConfigService
 *
 * Domain-layer facade over the Remote Config SDK wrapper
 * (`data/services/remoteConfig/remoteConfigDataService.ts`). Mirrors Flutter's
 * `RemoteConfigService` static surface (lib/services/remote_config_service.dart:16-329).
 *
 * **Every getter is synchronous and always returns a value.** It reads the live
 * activated Remote Config value when there is one, and otherwise falls back to
 * the compile-time `REMOTE_CONFIG_DEFAULTS` entry — which is what happens before
 * the first `fetchAndActivate()` lands, and permanently in a browser where
 * Remote Config is unsupported (no IndexedDB). That contract is deliberate: it
 * mirrors Flutter, where every consumer reads a plain static getter, and it lets
 * the call sites that used to read the hardcoded `REMOTE_CONFIG_CONSTANTS`
 * become one-line swaps instead of async refactors.
 *
 * Consequence to keep in mind (identical in Flutter): a value that arrives from
 * the server *after* a component has rendered does **not** re-render it. The
 * three paths Flutter itself makes reactive (budget-exceeded, sync-cache
 * invalidation, legal-document updates) are driven by the real-time listener
 * plumbed through `subscribeToUpdates` — not by these getters.
 *
 * Layering: this file owns the key enum, the defaults fallback, the int
 * truncation and the boosts JSON parse; the data service owns every
 * `firebase/remote-config` call. Nothing here imports from the presentation layer.
 */

import type { JsonObject } from '@/data/types/json';
import { remoteConfigDataService } from '@/data/services/remoteConfig/remoteConfigDataService';
import { REMOTE_CONFIG_DEFAULTS } from '@/domain/constants/remoteConfigDefaults';
import {
  RemoteConfigs,
  WebRemoteConfigs,
  type RemoteConfigKey,
} from '@/domain/enums/remoteConfigs';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class RemoteConfigService {
  private static instance: RemoteConfigService;

  /** Teardown closure for the real-time update stream (Flutter's `_remoteSubscription`). */
  private updatesUnsubscribe: (() => void) | null = null;

  private initialized = false;

  private constructor() { }

  public static getInstance(): RemoteConfigService {
    if (!RemoteConfigService.instance) {
      RemoteConfigService.instance = new RemoteConfigService();
    }
    return RemoteConfigService.instance;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Applies the settings + compile-time defaults and fetches/activates the
   * server template. Mirrors the first three steps of Flutter's `init()`
   * (lib/services/remote_config_service.dart:110-185).
   *
   * The `_handleConfigs()` step (:189) and the `subscribeToUpdates` call (:191)
   * are orchestrated by `InitializeRemoteConfigUseCase` so the effect handling
   * stays out of this service.
   *
   * `initialized` is set regardless of the data service's boolean result: an
   * unsupported browser still counts as "init attempted", and every getter then
   * simply keeps returning its compile-time default.
   *
   * `REMOTE_CONFIG_DEFAULTS` is passed straight through with **no cast** — it is
   * a `Partial<Record<RemoteConfigKey, …>>` whose optional properties are
   * `string | number | boolean | undefined`, exactly what `initialize` accepts
   * (it drops the `undefined` entries before Firebase sees them).
   */
  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    await remoteConfigDataService.initialize(REMOTE_CONFIG_DEFAULTS);
    this.initialized = true;
  }

  /**
   * Subscribes to real-time config updates and keeps the teardown closure.
   * Mirrors Flutter's `onConfigUpdated.listen` (remote_config_service.dart:191-194);
   * the `activate()` that must precede key processing already happens inside
   * `remoteConfigDataService.subscribeToConfigUpdates`, so this only forwards
   * the callback.
   *
   * Idempotent: a second call while a subscription is live is a no-op, so a
   * re-mounting listener component cannot stack duplicate streams.
   */
  subscribeToUpdates(onUpdatedKeys: (keys: string[]) => void | Promise<void>): void {
    if (this.updatesUnsubscribe) {
      return;
    }

    this.updatesUnsubscribe = remoteConfigDataService.subscribeToConfigUpdates(onUpdatedKeys);
  }

  /**
   * Cancels the real-time subscription and releases the RC instance.
   * Flutter's `close()` (remote_config_service.dart:327-329).
   */
  dispose(): void {
    this.updatesUnsubscribe?.();
    this.updatesUnsubscribe = null;
    remoteConfigDataService.dispose();
    this.initialized = false;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Read helpers — live value, else compile-time default, else typed literal
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Flutter: `_remoteConfig.getBool(key)` (remote_config_service.dart:20-27).
   * Falls back to `REMOTE_CONFIG_DEFAULTS[key]`, then to `false` (the Firebase
   * SDK's own static boolean default) when the key has no compile-time entry.
   */
  private readBool(key: RemoteConfigKey): boolean {
    const value = remoteConfigDataService.getBoolean(key);
    if (value !== null) {
      return value;
    }

    const fallback = REMOTE_CONFIG_DEFAULTS[key];
    return typeof fallback === 'boolean' ? fallback : false;
  }

  /**
   * Flutter: `_remoteConfig.getString(key)` (remote_config_service.dart:28-29).
   * Falls back to `REMOTE_CONFIG_DEFAULTS[key]`, then to `''` (the Firebase SDK's
   * own static string default). `boosts` is the only key that reaches here with
   * no compile-time entry, and its `''` result is exactly Flutter's empty-string
   * boosts path (remote_config_service.dart:86-88).
   */
  private readString(key: RemoteConfigKey): string {
    const value = remoteConfigDataService.getString(key);
    if (value !== null) {
      return value;
    }

    const fallback = REMOTE_CONFIG_DEFAULTS[key];
    return typeof fallback === 'string' ? fallback : '';
  }

  /**
   * Flutter: `_remoteConfig.getDouble(key)` (remote_config_service.dart:30-55).
   * Falls back to `REMOTE_CONFIG_DEFAULTS[key]`, then to `0` (the Firebase SDK's
   * own static number default).
   */
  private readDouble(key: RemoteConfigKey): number {
    const value = remoteConfigDataService.getNumber(key);
    if (value !== null) {
      return value;
    }

    const fallback = REMOTE_CONFIG_DEFAULTS[key];
    return typeof fallback === 'number' ? fallback : 0;
  }

  /**
   * Flutter's `_remoteConfig.getInt(key)` (remote_config_service.dart:56-81) has
   * **no web equivalent** — the JS SDK exposes only `getBoolean` / `getString` /
   * `getNumber`. The truncation Dart does implicitly is therefore explicit here.
   */
  private readInt(key: RemoteConfigKey): number {
    return Math.trunc(this.readDouble(key));
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Flutter-parity getters
  //
  // Deliberately absent (no web call site — an exported symbol with zero callers
  // is dead code): `useGoogleLoginOnApple`, `coinsToDiamondsRatio`,
  // `withdrawMinDollars`, `withdrawMaxDollars`, `withdrawAutoDollars`,
  // `filmMillisMin`, `shouldAutoInitVideos`, `exclusiveSubscriptionDefaultDollars`
  // and every `version*` key. Their keys and defaults still ship
  // (domain/constants/remoteConfigDefaults.ts) so the `defaultConfig` payload
  // matches the server template.
  //
  // `exclusiveSubscriptionDefaultDollars` in particular must NEVER get a getter
  // here: it is seeded from the compile-time `EXCLUSIVE_SUBSCRIPTION_DEFAULT_DOLLARS`
  // at `domain/services/auth/authService.ts:202`, per Flutter
  // `lib/data/models/user.dart:97-98` → `remote_configs_defaults.dart:19` — do not
  // read the live value here. Flutter's own live getter
  // (lib/services/remote_config_service.dart:48-49) likewise has zero callers;
  // sourcing that persisted `subscription_charge` field from the live value would
  // diverge the two clients the moment the console template is retuned.
  // ──────────────────────────────────────────────────────────────────────────

  /** Flutter: lib/services/remote_config_service.dart:22-23. */
  get initVideos(): boolean {
    return this.readBool(RemoteConfigs.initVideos);
  }

  /** Flutter: lib/services/remote_config_service.dart:24-25. */
  get budgetExceeded(): boolean {
    return this.readBool(RemoteConfigs.budgetExceeded);
  }

  /** Flutter: lib/services/remote_config_service.dart:26-27. */
  get isTestMode(): boolean {
    return this.readBool(RemoteConfigs.isTestMode);
  }

  /** Flutter: lib/services/remote_config_service.dart:62-63. */
  get lastSeenEnabled(): boolean {
    return this.readBool(RemoteConfigs.lastSeenEnabled);
  }

  /** Flutter: lib/services/remote_config_service.dart:28-29. */
  get defaultCurrency(): string {
    return this.readString(RemoteConfigs.defaultCurrency);
  }

  /** Flutter: lib/services/remote_config_service.dart:30-31. */
  get dollarToCoinsRatio(): number {
    return this.readDouble(RemoteConfigs.dollarToCoinsRatio);
  }

  /** Flutter: lib/services/remote_config_service.dart:34-35. */
  get diamondsToDollarsRatio(): number {
    return this.readDouble(RemoteConfigs.diamondsToDollarsRatio);
  }

  /** Flutter: lib/services/remote_config_service.dart:42-44. */
  get exclusiveSubscriptionMinDollars(): number {
    return this.readDouble(RemoteConfigs.exclusiveSubscriptionMinDollars);
  }

  /** Flutter: lib/services/remote_config_service.dart:45-47. */
  get exclusiveSubscriptionMaxDollars(): number {
    return this.readDouble(RemoteConfigs.exclusiveSubscriptionMaxDollars);
  }

  /** Flutter: lib/services/remote_config_service.dart:50-51. */
  get contentUnlockMinDollars(): number {
    return this.readDouble(RemoteConfigs.contentUnlockMinDollars);
  }

  /** Flutter: lib/services/remote_config_service.dart:52-53. */
  get contentUnlockMaxDollars(): number {
    return this.readDouble(RemoteConfigs.contentUnlockMaxDollars);
  }

  /** Flutter: lib/services/remote_config_service.dart:54-55. */
  get contentUnlockDefaultDollars(): number {
    return this.readDouble(RemoteConfigs.contentUnlockDefaultDollars);
  }

  /** Flutter: lib/services/remote_config_service.dart:56-58. */
  get preferredContentTagsWatchMinSeconds(): number {
    return this.readInt(RemoteConfigs.preferredContentTagsWatchMinSeconds);
  }

  /** Flutter: lib/services/remote_config_service.dart:59-61. */
  get preferredContentTagsWatchMinPercentage(): number {
    return this.readInt(RemoteConfigs.preferredContentTagsWatchMinPercentage);
  }

  /** Flutter: lib/services/remote_config_service.dart:64-65. */
  get updateViewsWatchMinSeconds(): number {
    return this.readInt(RemoteConfigs.updateViewsWatchMinSeconds);
  }

  /** Flutter: lib/services/remote_config_service.dart:66-67. */
  get saveToWatchHistoryMinSeconds(): number {
    return this.readInt(RemoteConfigs.saveToWatchHistoryMinSeconds);
  }

  /** Flutter: lib/services/remote_config_service.dart:70-71. */
  get contentMinSeconds(): number {
    return this.readInt(RemoteConfigs.contentMinSeconds);
  }

  /** Flutter: lib/services/remote_config_service.dart:72-73. */
  get contentMaxSeconds(): number {
    return this.readInt(RemoteConfigs.contentMaxSeconds);
  }

  /** Flutter: lib/services/remote_config_service.dart:74-75. */
  get freeContentMaxSeconds(): number {
    return this.readInt(RemoteConfigs.freeContentMaxSeconds);
  }

  /** Flutter: lib/services/remote_config_service.dart:76-77. */
  get premiumContentCoinsPerMinute(): number {
    return this.readInt(RemoteConfigs.premiumContentCoinsPerMinute);
  }

  /** Flutter: lib/services/remote_config_service.dart:78-79. */
  get contentScheduleMinHours(): number {
    return this.readInt(RemoteConfigs.contentScheduleMinHours);
  }

  /** Flutter: lib/services/remote_config_service.dart:80-81. */
  get contentScheduleMaxHours(): number {
    return this.readInt(RemoteConfigs.contentScheduleMaxHours);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Web-only getters (no Flutter counterpart; see `WebRemoteConfigs`)
  // ──────────────────────────────────────────────────────────────────────────

  /** Replaces `presentation/constants/remoteConfigConstants.ts:14`. */
  get webNativeFullscreenMode(): boolean {
    return this.readBool(WebRemoteConfigs.webNativeFullscreenMode);
  }

  /** Replaces `presentation/constants/remoteConfigConstants.ts:17`. */
  get shareContentMaxCharacters(): number {
    return this.readInt(WebRemoteConfigs.shareContentMaxCharacters);
  }

  /** Replaces `presentation/constants/remoteConfigConstants.ts:18`. */
  get shareUserMaxCharacters(): number {
    return this.readInt(WebRemoteConfigs.shareUserMaxCharacters);
  }

  /** Replaces `presentation/constants/remoteConfigConstants.ts:21`. */
  get chatContentMinSeconds(): number {
    return this.readInt(WebRemoteConfigs.chatContentMinSeconds);
  }

  /** Replaces `presentation/constants/remoteConfigConstants.ts:22`. */
  get chatContentMaxSeconds(): number {
    return this.readInt(WebRemoteConfigs.chatContentMaxSeconds);
  }

  /** Replaces `presentation/constants/remoteConfigConstants.ts:33`. */
  get contentTypeLimit(): number {
    return this.readInt(WebRemoteConfigs.contentTypeLimit);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Boosts
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * The raw boost entries from the `boosts` Remote Config key.
   *
   * Exact port of Flutter's `boosts` getter
   * (lib/services/remote_config_service.dart:84-108), including all four
   * degrade-to-empty guards — the server template is the only source of this
   * catalog, so a missing or malformed value must yield an empty list, never
   * throw:
   *  1. empty string          → `[]` (:86-88)
   *  2. not a JSON object     → `[]` (:92-94)
   *  3. `boosts` not a list   → `[]` (:96-99)
   *  4. parse threw           → log + `[]` (:104-107)
   *
   * Returns raw `JsonObject[]`; turning those into DTOs is the remapper's job
   * (`fromJson` is the only raw-JSON → DTO entry point).
   */
  getBoostsJson(): JsonObject[] {
    const raw = this.readString(RemoteConfigs.boosts);
    if (!raw) {
      return [];
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return [];
      }

      const list = (parsed as JsonObject).boosts;
      if (!Array.isArray(list)) {
        return [];
      }

      return list as JsonObject[];
    } catch (error) {
      appLogger.error(
        'Error parsing boosts from remote config',
        'RemoteConfigService.getBoostsJson',
        { error: error as Error },
      );
      return [];
    }
  }
}

export const remoteConfigService = RemoteConfigService.getInstance();
